import { useState, useEffect } from "react"
import { useNavigate } from 'react-router-dom'
import {
  Search,
  Filter,
  Plus,
  Eye,
  AlertCircle,
  Clock,
  CheckCircle,
  X,
  ChevronDown,
  Briefcase,
  Activity,
  RotateCcw,
  ListTodo,
  Trash2,
  Download,
  Save,
  Bookmark,
} from 'lucide-react'
import { toast } from 'sonner'
import { tasksService, Task } from '../services/tasks'
import { projectsService, Project } from '../services/projects'
import { part11Service, SavedView } from '../services/part11'
import { zeroTrustService } from '../services/enterpriseTrust'
import { useLabels } from '../contexts/LabelsContext'
import { StatusBadge } from '../components/StatusBadge'
import { getErrorMessage } from '../utils/apiTransformers'
import type { ApiErrorLike } from '../utils/apiError'

export default function TaskList() {
  const navigate = useNavigate()
  const { label } = useLabels()
  const [tasks, setTasks] = useState<Task[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [filterProject, setFilterProject] = useState<string>('all')
  // Custom-field list-view filtering: chips of "key:value" (or "key:op:value").
  const [cfFilters, setCfFilters] = useState<string[]>([])
  const [cfInput, setCfInput] = useState('')
  const [showDeleted, setShowDeleted] = useState(false)
  const [showFilters, setShowFilters] = useState(false)

  // A11.2 — bulk selection
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [bulkStatus, setBulkStatus] = useState('')
  const [bulkBusy, setBulkBusy] = useState(false)
  const [stepUp, setStepUp] = useState<null | (() => Promise<void>)>(null)
  const [pwd, setPwd] = useState('')

  // A11.3 — saved views
  const [views, setViews] = useState<SavedView[]>([])
  const [activeViewId, setActiveViewId] = useState<number | null>(null)

  useEffect(() => {
    fetchProjects()
    fetchTasks()
  }, [filterStatus, filterProject, showDeleted, cfFilters])

  useEffect(() => {
    loadViews()
  }, [])

  const loadViews = async () => {
    try {
      setViews(await part11Service.listViews('task'))
    } catch {
      /* views are optional — ignore load failures */
    }
  }

  const applyView = (v: SavedView) => {
    const cfg = (v.config || {}) as { filters?: Record<string, string> }
    const f = cfg.filters || {}
    setFilterStatus(f.status ?? 'all')
    setFilterProject(f.project ?? 'all')
    setSearchQuery(f.search ?? '')
    setActiveViewId(v.id)
  }

  const saveCurrentView = async () => {
    const name = prompt('Name this view:')
    if (!name) return
    try {
      await part11Service.createView({
        entity_type: 'task',
        name,
        config: { filters: { status: filterStatus, project: filterProject, search: searchQuery } },
      })
      toast.success('View saved')
      loadViews()
    } catch (e) {
      toast.error(getErrorMessage(e))
    }
  }

  const deleteView = async (id: number) => {
    try {
      await part11Service.deleteView(id)
      if (activeViewId === id) setActiveViewId(null)
      loadViews()
    } catch (e) {
      toast.error(getErrorMessage(e))
    }
  }

  const fetchProjects = async () => {
    try {
      const data = await projectsService.list()
      setProjects(data)
    } catch (error) {
      console.error('Error fetching projects:', error)
    }
  }

  const fetchTasks = async () => {
    setLoading(true)
    try {
      const params: Record<string, string | number | boolean | string[]> = {}
      if (filterStatus !== 'all') {
        params.status = filterStatus
      }
      if (filterProject !== 'all') {
        params.project_id = parseInt(filterProject)
      }
      if (showDeleted) {
        params.include_deleted = true
      }
      if (cfFilters.length > 0) {
        params.custom_field = cfFilters
      }
      const data = await tasksService.list(params)
      setTasks(data)
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      console.error('Error fetching tasks:', error)
      toast.error('Failed to load tasks. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this task?')) {
      return
    }
    try {
      await tasksService.delete(id)
      toast.success('Task deleted successfully')
      fetchTasks()
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      toast.error(error.response?.data?.detail || 'Failed to delete task')
    }
  }

  const handleRestore = async (id: number) => {
    try {
      await tasksService.restore(id)
      toast.success('Task restored successfully')
      fetchTasks()
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      toast.error(error.response?.data?.detail || 'Failed to restore task')
    }
  }

  const filteredTasks = tasks.filter((task) => {
    const matchesSearch =
      task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      task.description?.toLowerCase().includes(searchQuery.toLowerCase())
    return matchesSearch
  })

  // ── A11.2 bulk operations ──────────────────────────────────────────────
  const toggleOne = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const toggleAll = () => {
    setSelected((prev) =>
      prev.size === filteredTasks.length
        ? new Set()
        : new Set(filteredTasks.map((t) => t.id))
    )
  }

  const clearSelection = () => setSelected(new Set())
  const selectedIds = () => Array.from(selected)

  const applyBulkStatus = async () => {
    if (!bulkStatus) return
    setBulkBusy(true)
    try {
      const r = await part11Service.bulkTasks({ task_ids: selectedIds(), action: 'status', status: bulkStatus })
      toast.success(`Updated ${r.affected} task(s)`)
      clearSelection()
      setBulkStatus('')
      fetchTasks()
    } catch (e) {
      toast.error(getErrorMessage(e))
    } finally {
      setBulkBusy(false)
    }
  }

  const exportSelected = async () => {
    setBulkBusy(true)
    try {
      const blob = await part11Service.exportTasks(selectedIds())
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'tasks-export.csv'
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      toast.error(getErrorMessage(e))
    } finally {
      setBulkBusy(false)
    }
  }

  const bulkDelete = async () => {
    if (!confirm(`Delete ${selected.size} task(s)? This moves them to the Recycle Bin.`)) return
    const run = async () => {
      const r = await part11Service.bulkDeleteTasks(selectedIds())
      toast.success(`Deleted ${r.affected} task(s)`)
      clearSelection()
      fetchTasks()
    }
    setBulkBusy(true)
    try {
      await run()
    } catch (eRaw) {
      const e = eRaw as ApiErrorLike
      // A8b.5 — server may demand a fresh step-up before destructive bulk ops.
      if (e?.response?.headers?.['x-require-step-up'] === 'true') {
        setStepUp(() => run)
      } else {
        toast.error(getErrorMessage(e))
      }
    } finally {
      setBulkBusy(false)
    }
  }

  const confirmStepUp = async () => {
    try {
      await zeroTrustService.confirmStepUp('password', pwd)
      const retry = stepUp
      setStepUp(null)
      setPwd('')
      if (retry) {
        setBulkBusy(true)
        try { await retry() } finally { setBulkBusy(false) }
      }
    } catch (e) {
      toast.error(getErrorMessage(e))
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-700 border-red-300'
      case 'high':
        return 'bg-orange-100 text-orange-700 border-orange-300'
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-300'
      case 'low':
        return 'bg-gray-100 text-gray-700 border-gray-300'
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300'
    }
  }

  const getProjectName = (projectId: number) => {
    const project = projects.find((p) => p.id === projectId)
    return project?.name || `Project #${projectId}`
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString()
  }

  const stats = {
    total: tasks.length,
    todo: tasks.filter((t) => t.status === 'todo').length,
    inProgress: tasks.filter((t) => t.status === 'in_progress').length,
    done: tasks.filter((t) => t.status === 'done').length,
    blocked: tasks.filter((t) => t.status === 'blocked').length,
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 bg-[#f0f4f8] min-h-screen">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-[#2A3B4C] mb-2">{label('task')} Management</h1>
            <p className="text-base sm:text-lg text-[#556B7C]">View and manage all {label('task', 'plural').toLowerCase()} across your {label('project', 'plural').toLowerCase()}</p>
          </div>
          <button
            onClick={() => navigate('/task/create')}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all"
          >
            <Plus className="w-5 h-5" />
            <span>Create {label('task')}</span>
          </button>
        </div>
        {/* Stats Cards */}
        <div className="grid grid-cols-5 gap-4">
          {[
            { label: `Total ${label('task', 'plural')}`, value: stats.total.toString(), icon: ListTodo, iconColor: 'bg-blue-50' },
            { label: 'To Do', value: stats.todo.toString(), icon: Clock, iconColor: 'bg-gray-50' },
            { label: 'In Progress', value: stats.inProgress.toString(), icon: Activity, iconColor: 'bg-blue-50' },
            { label: 'Done', value: stats.done.toString(), icon: CheckCircle, iconColor: 'bg-green-50' },
            { label: 'Blocked', value: stats.blocked.toString(), icon: AlertCircle, iconColor: 'bg-red-50' },
          ].map((stat, index) => {
            const Icon = stat.icon
            return (
              <div key={index} className="bg-white rounded-xl p-4 shadow-md border border-gray-100">
                <div className="flex items-start justify-between mb-2">
                  <div className="text-sm text-[#6F8394]">{stat.label}</div>
                  <div className={`p-2 rounded-lg ${stat.iconColor}`}>
                    <Icon className="w-4 h-4 text-blue-600" />
                  </div>
                </div>
                <div className="text-2xl font-semibold text-[#2A3B4C]">{stat.value}</div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-4 rounded-xl border-2 border-[#bcccdc] shadow-md">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#556B7C] w-5 h-5" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search tasks by title or description..."
              className="w-full pl-10 pr-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#2A3B4C]"
            />
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-4 py-2 bg-[#f0f4f8] hover:bg-[#e0e8f0] border-2 border-[#bcccdc] rounded-lg transition-colors flex items-center gap-2"
          >
            <Filter className="w-4 h-4" />
            Filters
            <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>

          {/* Show Deleted Toggle */}
          <button
            onClick={() => setShowDeleted(!showDeleted)}
            className={`px-4 py-2 border-2 rounded-lg transition-colors flex items-center gap-2 ${
              showDeleted
                ? 'bg-[#486581] text-white border-[#486581]'
                : 'bg-white border-[#bcccdc] hover:bg-[#f0f4f8]'
            }`}
          >
            <RotateCcw className="w-4 h-4" />
            {showDeleted ? 'Hide Deleted' : 'Show Deleted'}
          </button>
        </div>

        {/* Expanded Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-[#bcccdc] grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#102a43] mb-2">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#2A3B4C]"
              >
                <option value="all">All Statuses</option>
                <option value="todo">To Do</option>
                <option value="in_progress">In Progress</option>
                <option value="in_review">In Review</option>
                <option value="done">Done</option>
                <option value="blocked">Blocked</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#102a43] mb-2">Project</label>
              <select
                value={filterProject}
                onChange={(e) => setFilterProject(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#2A3B4C]"
              >
                <option value="all">All Projects</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id.toString()}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Custom-field filter: add "key:value" (or "key:op:value", op=eq|ne|contains) */}
            <div className="md:col-span-2">
              <label htmlFor="cf-filter" className="block text-sm font-medium text-[#102a43] mb-2">
                Custom field filter
              </label>
              <div className="flex gap-2">
                <input
                  id="cf-filter"
                  type="text"
                  value={cfInput}
                  onChange={(e) => setCfInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && cfInput.includes(':')) {
                      setCfFilters((prev) => Array.from(new Set([...prev, cfInput.trim()])))
                      setCfInput('')
                    }
                  }}
                  placeholder="trade:electrical  or  amount:ne:0"
                  className="flex-1 px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#2A3B4C]"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (cfInput.includes(':')) {
                      setCfFilters((prev) => Array.from(new Set([...prev, cfInput.trim()])))
                      setCfInput('')
                    }
                  }}
                  className="px-4 py-2 bg-[#486581] text-white rounded-lg text-sm hover:bg-[#3d5570] transition-colors"
                >
                  Add
                </button>
              </div>
              {cfFilters.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {cfFilters.map((chip) => (
                    <span
                      key={chip}
                      className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#e0e7ef] text-[#2A3B4C] rounded-full text-xs"
                    >
                      {chip}
                      <button
                        type="button"
                        onClick={() => setCfFilters((prev) => prev.filter((c) => c !== chip))}
                        aria-label={`Remove filter ${chip}`}
                        className="hover:text-red-600"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* A11.3 — Saved views */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-[#556B7C] flex items-center gap-1">
          <Bookmark className="w-4 h-4" /> Views:
        </span>
        {views.length === 0 && <span className="text-sm text-[#90a4b7]">none saved</span>}
        {views.map((v) => (
          <span
            key={v.id}
            className={`group flex items-center gap-1 px-3 py-1 rounded-full text-sm border cursor-pointer transition-colors ${
              activeViewId === v.id
                ? 'bg-[#486581] text-white border-[#486581]'
                : 'bg-white border-[#bcccdc] text-[#2A3B4C] hover:bg-[#f0f4f8]'
            }`}
            onClick={() => applyView(v)}
          >
            {v.name}
            {v.is_shared && <span className="text-xs opacity-70">(shared)</span>}
            <button
              onClick={(e) => { e.stopPropagation(); deleteView(v.id) }}
              className="opacity-0 group-hover:opacity-100 transition-opacity"
              title="Delete view"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <button
          onClick={saveCurrentView}
          className="flex items-center gap-1 px-3 py-1 rounded-full text-sm border-2 border-dashed border-[#bcccdc] text-[#556B7C] hover:bg-[#f0f4f8]"
        >
          <Save className="w-3 h-3" /> Save current
        </button>
      </div>

      {/* A11.2 — Bulk action toolbar */}
      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 bg-[#1a2942] text-white p-3 rounded-xl shadow-md">
          <span className="text-sm font-medium">{selected.size} selected</span>
          <select
            value={bulkStatus}
            onChange={(e) => setBulkStatus(e.target.value)}
            className="px-3 py-1.5 rounded-lg text-sm text-[#2A3B4C]"
          >
            <option value="">Set status…</option>
            <option value="todo">To Do</option>
            <option value="in_progress">In Progress</option>
            <option value="review">In Review</option>
            <option value="done">Done</option>
            <option value="blocked">Blocked</option>
          </select>
          <button
            onClick={applyBulkStatus}
            disabled={!bulkStatus || bulkBusy}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm disabled:opacity-50"
          >
            Apply
          </button>
          <button
            onClick={exportSelected}
            disabled={bulkBusy}
            className="flex items-center gap-1 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-sm"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
          <button
            onClick={bulkDelete}
            disabled={bulkBusy}
            className="flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded-lg text-sm"
          >
            <Trash2 className="w-4 h-4" /> Delete
          </button>
          <button onClick={clearSelection} className="ml-auto text-sm text-white/70 hover:text-white">
            Clear
          </button>
        </div>
      )}

      {/* Step-up re-auth modal for destructive bulk delete (A8b.5) */}
      {stepUp && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl p-5 w-full max-w-sm shadow-2xl">
            <h3 className="font-semibold text-[#2A3B4C] mb-2">Confirm your password</h3>
            <p className="text-sm text-[#556B7C] mb-3">
              This action requires recent authentication.
            </p>
            <input
              type="password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              placeholder="Password"
              className="w-full px-3 py-2 border-2 border-[#bcccdc] rounded-lg mb-3"
            />
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => { setStepUp(null); setPwd('') }}
                className="px-3 py-1.5 text-sm text-[#556B7C]"
              >
                Cancel
              </button>
              <button
                onClick={confirmStepUp}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tasks Table */}
      <div className="bg-white rounded-xl border-2 border-[#bcccdc] shadow-md overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-[#486581] border-t-transparent"></div>
            <p className="mt-4 text-[#556B7C]">Loading tasks...</p>
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="p-12 text-center">
            <ListTodo className="w-16 h-16 text-[#bcccdc] mx-auto mb-4" />
            <p className="text-lg font-medium text-[#2A3B4C] mb-2">No tasks found</p>
            <p className="text-[#556B7C] mb-4">
              {searchQuery || filterStatus !== 'all' || filterProject !== 'all'
                ? 'Try adjusting your filters'
                : 'Get started by creating your first task'}
            </p>
            {!searchQuery && filterStatus === 'all' && filterProject === 'all' && (
              <button
                onClick={() => navigate('/task/create')}
                className="px-4 py-2 bg-[#486581] text-white rounded-lg hover:bg-[#5a7692] transition-colors flex items-center gap-2 mx-auto"
              >
                <Plus className="w-4 h-4" />
                Create Task
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <caption className="sr-only">List of tasks</caption>
              <thead className="bg-[#f0f4f8] border-b-2 border-[#bcccdc]">
                <tr>
                  <th scope="col" className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={filteredTasks.length > 0 && selected.size === filteredTasks.length}
                      onChange={toggleAll}
                      aria-label="Select all tasks"
                    />
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#102a43] uppercase tracking-wider">Task</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#102a43] uppercase tracking-wider">Project</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#102a43] uppercase tracking-wider">Status</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#102a43] uppercase tracking-wider">Priority</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#102a43] uppercase tracking-wider">Due Date</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#102a43] uppercase tracking-wider">Hours</th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-[#102a43] uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#bcccdc]">
                {filteredTasks.map((task) => (
                  <tr key={task.id} className="hover:bg-[#f0f4f8] transition-colors">
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selected.has(task.id)}
                        onChange={() => toggleOne(task.id)}
                        aria-label={`Select ${task.title}`}
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-[#2A3B4C]">{task.title}</p>
                        {task.description && (
                          <p className="text-xs text-[#556B7C] mt-1 line-clamp-1">{task.description}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Briefcase className="w-4 h-4 text-[#556B7C]" />
                        <span className="text-sm text-[#2A3B4C]">{getProjectName(task.project_id)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge entityType="task" status={task.status} className="!rounded-full" />
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(task.priority)}`}>
                        {task.priority}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-[#556B7C]" />
                        <span className="text-sm text-[#2A3B4C]">{formatDate(task.due_date)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-[#2A3B4C]">
                        {task.actual_hours ? (
                          <>
                            {task.actual_hours}h
                            {task.estimated_hours && ` / ${task.estimated_hours}h`}
                          </>
                        ) : task.estimated_hours ? (
                          `${task.estimated_hours}h (est.)`
                        ) : (
                          '-'
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => navigate(`/task/${task.id}`)}
                          className="p-2 hover:bg-[#f0f4f8] rounded-lg transition-colors"
                          title="View task"
                        >
                          <Eye className="w-4 h-4 text-[#486581]" />
                        </button>
                        {showDeleted ? (
                          <button
                            onClick={() => handleRestore(task.id)}
                            className="p-2 hover:bg-green-50 rounded-lg transition-colors"
                            title="Restore task"
                          >
                            <RotateCcw className="w-4 h-4 text-green-600" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleDelete(task.id)}
                            className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete task"
                          >
                            <X className="w-4 h-4 text-red-600" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

